/**
 * ContractStatusWidget Component
 *
 * Dashboard widget showing contract statistics and recent activity.
 * Only visible for Rise+ tier users with contracts feature enabled.
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  LinearProgress,
  Skeleton,
} from '@mui/material';
import {
  Description as ContractIcon,
  Send as SentIcon,
  Visibility as ViewedIcon,
  CheckCircle as SignedIcon,
  Warning as ExpiredIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { WidgetCard } from './WidgetCard';
import { contractsApi } from '../../../services/contractsApi';
import { useAuth } from '../../../contexts/AuthContext';

interface StatRowProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  color?: 'primary' | 'warning' | 'success' | 'error' | 'info';
  onClick?: () => void;
}

const StatRow: React.FC<StatRowProps> = ({ icon, label, count, color = 'primary', onClick }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    py={0.75}
    px={1}
    sx={{
      borderRadius: 1,
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick
        ? {
            bgcolor: 'action.hover',
          }
        : {},
    }}
    onClick={onClick}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      {icon}
      <Typography variant="body2">{label}</Typography>
    </Stack>
    <Chip
      label={count}
      size="small"
      color={count > 0 ? color : 'default'}
      variant={count > 0 ? 'filled' : 'outlined'}
      sx={{ minWidth: 32, fontWeight: 600 }}
    />
  </Box>
);

export const ContractStatusWidget: React.FC = () => {
  const navigate = useNavigate();
  const { organization } = useAuth();

  // Check if contracts feature is enabled
  const contractsEnabled = organization?.boldsign?.contractsEnabled === true;

  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: ['contractStats'],
    queryFn: contractsApi.getStats,
    enabled: contractsEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleNavigate = (status?: string) => {
    if (status) {
      navigate(`/contracts?status=${status}`);
    } else {
      navigate('/contracts');
    }
  };

  // If contracts not enabled, show a teaser
  if (!contractsEnabled) {
    return (
      <WidgetCard
        title="Contracts"
        icon={<ContractIcon color="action" />}
        minHeight={180}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          py={2}
          textAlign="center"
        >
          <ContractIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
          <Typography variant="body2" color="text.secondary" mb={1}>
            Contracts feature not yet enabled
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Contact support to enable e-signatures
          </Typography>
        </Box>
      </WidgetCard>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <WidgetCard
        title="Contracts"
        icon={<ContractIcon color="primary" />}
        minHeight={180}
      >
        <Stack spacing={1} py={1}>
          <Skeleton variant="rounded" height={36} />
          <Skeleton variant="rounded" height={36} />
          <Skeleton variant="rounded" height={36} />
          <Skeleton variant="rounded" height={36} />
        </Stack>
      </WidgetCard>
    );
  }

  // Calculate completion rate
  const completedCount = stats?.completed || 0;
  const totalSent = (stats?.sent || 0) + (stats?.viewed || 0) + (stats?.signed || 0) + completedCount + (stats?.declined || 0) + (stats?.expired || 0) + (stats?.revoked || 0);
  const completionRate = totalSent > 0 ? Math.round((completedCount / totalSent) * 100) : 0;

  return (
    <WidgetCard
      title="Contracts"
      icon={<ContractIcon color="primary" />}
      isLoading={isLoading}
      error={error as Error | null}
      onRefresh={() => refetch()}
      viewAllLink="/contracts"
      viewAllLabel="View All Contracts"
      minHeight={180}
      action={
        (stats?.pendingCount || 0) > 0 ? (
          <Chip
            label={`${stats?.pendingCount} Pending`}
            color="warning"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        ) : undefined
      }
    >
      <Box>
        {/* Summary Stats */}
        <Stack spacing={0.5}>
          <StatRow
            icon={<SentIcon fontSize="small" sx={{ color: 'info.main' }} />}
            label="Awaiting Signature"
            count={(stats?.sent || 0) + (stats?.viewed || 0)}
            color="warning"
            onClick={() => handleNavigate('sent')}
          />
          <StatRow
            icon={<ViewedIcon fontSize="small" sx={{ color: 'primary.main' }} />}
            label="Viewed"
            count={stats?.viewed || 0}
            color="info"
            onClick={() => handleNavigate('viewed')}
          />
          <StatRow
            icon={<SignedIcon fontSize="small" sx={{ color: 'success.main' }} />}
            label="Completed"
            count={stats?.completed || 0}
            color="success"
            onClick={() => handleNavigate('completed')}
          />
          <StatRow
            icon={<ExpiredIcon fontSize="small" sx={{ color: 'error.main' }} />}
            label="Needs Attention"
            count={(stats?.declined || 0) + (stats?.expired || 0)}
            color="error"
            onClick={() => handleNavigate('declined')}
          />
        </Stack>

        {/* Completion Rate */}
        {totalSent > 0 && (
          <Box mt={2} px={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="caption" color="text.secondary">
                Completion Rate
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {completionRate}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={completionRate}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  bgcolor: completionRate >= 70 ? 'success.main' : completionRate >= 40 ? 'warning.main' : 'error.main',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </WidgetCard>
  );
};

export default ContractStatusWidget;
